import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Check, X, Tag } from "lucide-react";
import { usePromoCode, formatDiscount } from "@/lib/billing/usePromoCode";

interface Props {
  onApplied: (code: string | null) => void;
  context?: "signup" | "checkout";
  /** Whether to record first-touch attribution server-side. Default true. */
  attribute?: boolean;
}

const PromoCodeInput = ({ onApplied, context = "checkout", attribute = true }: Props) => {
  const [code, setCode] = useState("");
  const [open, setOpen] = useState(false);
  const { apply, clear, applied, busy, error } = usePromoCode();

  const handleApply = async () => {
    const res = await apply(code, { attribute, context });
    if (res?.valid) onApplied(res.code);
  };

  const handleClear = () => {
    clear();
    setCode("");
    onApplied(null);
  };

  if (!open && !applied) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1.5"
      >
        <Tag className="h-3 w-3" /> Have a referral or promo code?
      </button>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        <Input
          value={code}
          onChange={(e) => setCode(e.target.value.toUpperCase())}
          placeholder="WAVE-LUNA10"
          disabled={busy || (applied?.valid ?? false)}
          className="font-mono text-sm uppercase"
          maxLength={32}
        />
        {applied?.valid ? (
          <Button type="button" variant="outline" size="sm" onClick={handleClear}>
            <X className="h-4 w-4" />
          </Button>
        ) : (
          <Button type="button" size="sm" onClick={handleApply} disabled={busy || code.length < 3}>
            {busy ? "…" : "Apply"}
          </Button>
        )}
      </div>
      {applied?.valid && (
        <p className="text-xs text-success inline-flex items-center gap-1.5">
          <Check className="h-3 w-3" />
          {formatDiscount(applied)}
          {applied.influencer_handle && ` · via @${applied.influencer_handle}`}
        </p>
      )}
      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  );
};

export default PromoCodeInput;
